import { BigDecimal } from "@solomon-labs/bigdecimal";
import { WELL_KNOWN_TOKENS_STRINGS } from "@solomon-labs/constants";
import type { PublicKey } from "@solomon-labs/publickey";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import { SolomonVault } from "@solomon-labs/solomon-protocol";
import { create } from "zustand";

import mSOLLogo from "../assets/icons/tokens/msol.svg";
import PYUSDLogo from "../assets/icons/tokens/pyusd.svg";
import SOLLogo from "../assets/icons/tokens/sol.svg";
import sUSDvLogo from "../assets/icons/tokens/sUSDv.svg";
import USD1Logo from "../assets/icons/tokens/USD1.svg";
import USDCLogo from "../assets/icons/tokens/USDC.svg";
import USDGLogo from "../assets/icons/tokens/usdg.svg";
import USDTLogo from "../assets/icons/tokens/USDT.svg";
import USDvLogo from "../assets/logos/usdv-logomark.png";
import { USDV_DECIMALS, USDV_MINT } from "../tokens/usdv";
import type { SessionState, Token, TokenType } from "../types/session";
import type { ParticipantAccountStatus } from "../types/yaas";
import { readSolomonE2EChainOverride } from "../wallet/e2e";

/**
 * Token decimals for balance formatting
 */
const TOKEN_DECIMALS: Record<string, number> = {
    sUSDv: 9,
    USDC: 6,
    USDT: 6,
    SOL: 9,
    mSOL: 9,
    PYUSD: 6,
    USD1: 6,
    USDG: 6
};

/**
 * Additional token addresses not provided by WELL_KNOWN_TOKENS_STRINGS.
 */
const ADDITIONAL_TOKEN_ADDRESSES = {
    mSOL: "mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So",
    USD1: "USD1m8esQPzYdSYDZT9qvZf3msjrJqDqKiKSmRJQPro",
    USDG: "2u1tszSeqZ3qBWF3uNGPFc8TzMk2tdiwknnRMWGWjGWH"
} as const;

/**
 * Build Token array from WELL_KNOWN_TOKENS
 */
function buildTokenList(): Token[] {
    const tokens: Token[] = [];

    // Normal token selection must only expose the current Token-2022 USDv.
    tokens.push({
        address: USDV_MINT,
        name: "USDv",
        symbol: "USDv",
        decimals: USDV_DECIMALS,
        uri: "https://solomonlabs.org/assets/usdv.json",
        logo: USDvLogo,
        types: ["MainStable"] as TokenType[]
    });

    // sUSDv
    if (WELL_KNOWN_TOKENS_STRINGS.sUSDv) {
        tokens.push({
            address: WELL_KNOWN_TOKENS_STRINGS.sUSDv,
            name: "sUSDv",
            symbol: "sUSDv",
            decimals: TOKEN_DECIMALS.sUSDv,
            uri: "https://solomonlabs.org/assets/susdv.json",
            logo: sUSDvLogo,
            types: ["StakedMainStable"] as TokenType[]
        });
    }

    // USDC
    if (WELL_KNOWN_TOKENS_STRINGS.USDC) {
        tokens.push({
            address: WELL_KNOWN_TOKENS_STRINGS.USDC,
            name: "USD Coin",
            symbol: "USDC",
            decimals: TOKEN_DECIMALS.USDC,
            uri: "",
            logo: USDCLogo,
            types: ["MintableAsset", "RedeemableAsset"] as TokenType[]
        });
    }

    // USDT
    if (WELL_KNOWN_TOKENS_STRINGS.USDT) {
        tokens.push({
            address: WELL_KNOWN_TOKENS_STRINGS.USDT,
            name: "USDT",
            symbol: "USDT",
            decimals: TOKEN_DECIMALS.USDT,
            uri: "",
            logo: USDTLogo,
            types: ["MintableAsset", "RedeemableAsset"] as TokenType[]
        });
    }

    // SOL
    if (WELL_KNOWN_TOKENS_STRINGS.SOL) {
        tokens.push({
            address: WELL_KNOWN_TOKENS_STRINGS.SOL,
            name: "SOL",
            symbol: "SOL",
            decimals: TOKEN_DECIMALS.SOL,
            uri: "",
            logo: SOLLogo,
            types: ["MintableAsset", "RedeemableAsset"] as TokenType[]
        });
    }

    // mSOL
    tokens.push({
        address: ADDITIONAL_TOKEN_ADDRESSES.mSOL,
        name: "Marinade staked SOL (mSOL)",
        symbol: "mSOL",
        decimals: TOKEN_DECIMALS.mSOL,
        uri: "",
        logo: mSOLLogo,
        types: ["MintableAsset", "RedeemableAsset"] as TokenType[]
    });

    // PYUSD
    if (WELL_KNOWN_TOKENS_STRINGS.PYUSD) {
        tokens.push({
            address: WELL_KNOWN_TOKENS_STRINGS.PYUSD,
            name: "PayPal USD",
            symbol: "PYUSD",
            decimals: TOKEN_DECIMALS.PYUSD,
            uri: "https://token-metadata.paxos.com/pyusd_metadata/prod/solana/pyusd_metadata.json",
            logo: PYUSDLogo,
            types: ["MintableAsset", "RedeemableAsset"] as TokenType[]
        });
    }

    // USD1
    tokens.push({
        address: ADDITIONAL_TOKEN_ADDRESSES.USD1,
        name: "World Liberty Financial USD",
        symbol: "USD1",
        decimals: TOKEN_DECIMALS.USD1,
        uri: "",
        logo: USD1Logo,
        types: ["MintableAsset", "RedeemableAsset"] as TokenType[]
    });

    // USDG
    tokens.push({
        address: ADDITIONAL_TOKEN_ADDRESSES.USDG,
        name: "Global Dollar",
        symbol: "USDG",
        decimals: TOKEN_DECIMALS.USDG,
        uri: "https://token-metadata.paxos.com/usdg_metadata/prod/solana/usdg_metadata.json",
        logo: USDGLogo,
        types: ["MintableAsset", "RedeemableAsset"] as TokenType[]
    });

    return tokens;
}

/**
 * Session store actions
 */
interface SessionActions {
    initialize: () => void;
    reset: () => void;
    getTokens: () => Token[];
    getSolBalance: (owner: PublicKey) => Promise<void>;
    getVaultPermissions: (owner: PublicKey) => Promise<void>;
    setYaasStatus: (status: ParticipantAccountStatus | null) => void;
    resetYaasStatus: () => void;
}

/**
 * Extended session state with YaaS status
 */
interface ExtendedSessionState extends SessionState {
    yaasStatus: ParticipantAccountStatus | null;
    isYaasStatusLoaded: boolean;
}

export type SessionStore = ExtendedSessionState & SessionActions;

/**
 * Initial state
 */
const INITIAL_STATE: ExtendedSessionState = {
    tokens: [],
    tokenAddresses: [],
    solBalance: null,
    isSolBalanceLoaded: false,
    vaultPermissions: undefined,
    isVaultPermissionsLoaded: false,
    isError: false,
    yaasStatus: null,
    isYaasStatusLoaded: false
};

/**
 * Session store for managing user session data
 * - Token list management
 * - SOL balance
 * - Vault permissions
 * - YaaS status
 */
export const useSessionStore = create<SessionStore>((set, get) => ({
    ...INITIAL_STATE,

    /**
     * Initialize the store with token list
     */
    initialize: (): void => {
        const tokens = buildTokenList();
        const tokenAddresses = tokens.map((t) => t.address);
        set({ tokens, tokenAddresses });
    },

    /**
     * Reset store to initial state
     */
    reset: (): void => {
        set(INITIAL_STATE);
    },

    /**
     * Get the token list
     */
    getTokens: (): Token[] => {
        const { tokens } = get();
        if (tokens.length === 0) {
            const newTokens = buildTokenList();
            const tokenAddresses = newTokens.map((t) => t.address);
            set({ tokens: newTokens, tokenAddresses });
            return newTokens;
        }
        return tokens;
    },

    /**
     * Fetch SOL balance for the given owner
     */
    getSolBalance: async (owner: PublicKey): Promise<void> => {
        set({ isSolBalanceLoaded: false });

        try {
            const balance = await ConnectionManager.getBalance(owner);
            const solBalance = new BigDecimal(balance.toString()).divide(
                new BigDecimal(10).pow(9) // LAMPORTS_PER_SOL = 10^9
            );
            set({ solBalance, isSolBalanceLoaded: true });
        } catch (error) {
            console.error("Error fetching SOL balance:", error);
            set({ isError: true, isSolBalanceLoaded: true });
        }
    },

    /**
     * Fetch vault permissions for the given owner using SolomonVault SDK
     */
    getVaultPermissions: async (owner: PublicKey): Promise<void> => {
        set({ isVaultPermissionsLoaded: false });

        try {
            const e2eChainOverride = readSolomonE2EChainOverride();
            if (e2eChainOverride?.vaultPermissions) {
                set({
                    vaultPermissions: {
                        key: owner,
                        canMint: e2eChainOverride.vaultPermissions.canMint,
                        canRedeem: e2eChainOverride.vaultPermissions.canRedeem
                    },
                    isVaultPermissionsLoaded: true
                });
                return;
            }

            // Use SolomonVault SDK to fetch user permissions
            const permissions = await SolomonVault.fetchUserPermissions(owner);

            if (permissions) {
                set({
                    vaultPermissions: {
                        key: permissions.key as PublicKey,
                        canMint: permissions.canMint,
                        canRedeem: permissions.canRedeem
                    },
                    isVaultPermissionsLoaded: true
                });
            } else {
                // User not whitelisted - no permissions PDA exists
                set({
                    vaultPermissions: {
                        key: owner,
                        canMint: false,
                        canRedeem: false
                    },
                    isVaultPermissionsLoaded: true
                });
            }
        } catch (error) {
            console.error("Error fetching vault permissions:", error);
            // On error, default to no permissions
            set({
                vaultPermissions: {
                    key: owner,
                    canMint: false,
                    canRedeem: false
                },
                isError: true,
                isVaultPermissionsLoaded: true
            });
        }
    },

    /**
     * Set YaaS account status
     */
    setYaasStatus: (status: ParticipantAccountStatus | null): void => {
        set({ yaasStatus: status, isYaasStatusLoaded: true });
    },

    /**
     * Reset YaaS account status state (used on wallet disconnect/switch)
     */
    resetYaasStatus: (): void => {
        set({ yaasStatus: null, isYaasStatusLoaded: false });
    }
}));
